import { useState, useMemo, useEffect, useRef } from "react";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { X, ArrowLeft, Eye } from "lucide-react";
import { TicketAttachmentPdfPreview } from "@sd/components/tickets/AttachmentViewer";
import ServiceDeskAPIService, {
  ptsOpenProjectListQueryKey,
  ptsTaskListQueryKey,
  ptsWorkTypeListQueryKey,
  serviceDeskAgentAssignedProjectsQueryKey,
  serviceDeskEmployeeListQueryKey,
  serviceDeskUserProjectQueryKey,
} from "@sd/services/APIs";
import {
  ensurePtsSelectedOption,
  ptsDropdownOptionsFromPayload,
  ptsTaskOptionsFromPayload,
  PTS_PORTAL_SUBTASK_CREATE_STATUS_ID,
  ptsWorkTypeOptionsFromPayload,
} from "@sd/lib/ptsDropdownOptions";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { normalizeDynamicModuleUploadFields } from "@/lib/normalizeDynamicModuleUploadFields";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { handleSubjectInputPaste } from "@/lib/pastePlainText";
import { sanitizeInput, decodeHtmlEntitiesInRecord } from "@sd/lib/sdSanitizer";
import {
  plainTextToRichHtml,
  richTextPlainText,
  richTextPlainTextLength,
  sanitizeRichTextHtml,
} from "@sd/lib/richTextUtils";
import { TicketDescriptionRichTextEditor } from "@sd/components/common/TicketDescriptionRichTextEditor";
import {
  mimeFromAttachmentFilename,
  parseTicketAttachmentsFromRecord,
} from "@sd/lib/parseAttachmentIds";
import type { Attachment } from "@sd/types/ticket";
import {
  TICKET_TYPE_CREATE_OPTIONS,
  ticketTypeForApi,
} from "@sd/types/ticket";
import { parsePtsFromTicketRecord } from "@sd/lib/parsePtsFromTicketRecord";
import {
  appendChangeRequestFormFields,
  EMPTY_CHANGE_REQ_DATA,
  extractCreatedChangeRequestNumber,
  findChangeRequestObject,
  mergeParsedChangeRequest,
  parseChangeRequestFromTicketRecord,
} from "@sd/lib/changeRequestTicketFields";
import { normalizeTicketCreateDetailPayload } from "@sd/lib/ticketCreateDetail";
import {
  AuthenticatedMediaImage,
  AuthenticatedMediaVideo,
} from "@/components/common/AuthenticatedMediaImage";
import { useAttachmentFilesByIds } from "@sd/hooks/useAttachmentFilesByIds";
import { collectAttachmentMediaPathsFromRecord } from "@sd/lib/attachmentMediaUrl";
import {
  captureViewport,
  captureScreen,
  startScreenRecording,
  RecordingHandle,
} from "@sd/lib/sdCapture";

import {
  IMAGE_UPLOAD_ERROR_TITLE,
  IMAGE_UPLOAD_VALIDATION_MESSAGE,
  TICKET_ATTACHMENT_DOCUMENT_MAX_BYTES,
  TICKET_ATTACHMENT_IMAGE_MAX_BYTES,
  TICKET_ATTACHMENT_UPLOAD_ACCEPT,
  TICKET_ATTACHMENT_UPLOAD_HINT,
  TICKET_ATTACHMENT_VIDEO_MAX_BYTES,
  TICKET_ATTACHMENT_VIDEO_MAX_MB,
  imageUploadRejectedMessage,
  isAllowedDocumentUpload,
  isAllowedImageUpload,
  isAllowedTicketAttachmentUpload,
  isAllowedVideoUpload,
  isImageDataUrl,
  pickTicketAttachmentUploadFiles,
  ticketAttachmentRejectedMessage,
} from "@sd/lib/imageUploadValidation";
import {
  clampTicketSubject,
  SUBJECT_MAX_LENGTH,
  subjectMaxLengthError,
} from "@sd/lib/ticketFormConstants";
import { formatPriorityLabel } from "@sd/lib/ticketStreamUtils";
import {
  TICKET_CATEGORY_FILTER_OPTIONS,
  normalizeCategoryFilterValue,
} from "@sd/lib/ticketCategoryOptions";
import { getServiceDeskUserId, useAuth } from "@sd/hooks/useAuth";
import {
  isAgentShellRole,
  isExternalSdRole,
  SD_EXTERNAL_AGENT_ROLE,
} from "@sd/lib/sdRoles";
import { employeeListAssignSelectOptions } from "@sd/lib/ticketRequesterFilter";
import { cn } from "@/lib/utils";
import { ConfigProvider } from "antd";
import PtsListSelect from "./PtsListSelect";
import "./styles.css";

const SD_WIDGET_THEME = {
  token: {
    colorPrimary: "#f97316",
    colorPrimaryHover: "#fb923c",
    colorPrimaryActive: "#ea6500",
    colorLink: "#f97316",
    borderRadius: 8,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif",
  },
};

const SD_AGENT_THEME = {
  token: {
    colorPrimary: "#1e293b",
    colorPrimaryHover: "#334155",
    colorPrimaryActive: "#0f172a",
    colorLink: "#1e293b",
    borderRadius: 8,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif",
  },
};

// ─── Dropdown Options ─────────────────────────────────────────────────────────
const CATEGORIES = TICKET_CATEGORY_FILTER_OPTIONS.map(opt => ({
  value: opt.value,
  label: opt.label,
}));

const DESCRIPTION_MAX_LENGTH = 2000;

/** Map API tickettype → create-form type value (create / copy prefill). */
function mapFormTypeFromTicketRecord(r: Record<string, unknown>): string {
  const rawType = String(r.tickettype ?? r.ticket_type ?? "")
    .trim()
    .toLowerCase();
  if (rawType === "issue") return "issue";
  if (rawType === "incident" || rawType === "insident") return "issue";
  if (
    rawType === "new_requirement" ||
    rawType.includes("new requirement") ||
    rawType.includes("requirement")
  ) {
    return "new_requirement";
  }
  if (rawType === "request" || rawType.includes("service request")) {
    return "request";
  }
  if (rawType === "suggestion") return "suggestion";
  return "";
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function asUuid(value: unknown): string | null {
  const text = String(value ?? "").trim();
  return UUID_RE.test(text) ? text : null;
}

/** Best-effort UUID from POST `/service_desk/ticket_create/` response shapes. */
function extractCreatedTicketId(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const root = raw as Record<string, unknown>;
  const nested =
    root.ticket && typeof root.ticket === "object" && !Array.isArray(root.ticket)
      ? (root.ticket as Record<string, unknown>)
      : null;
  const data =
    root.data && typeof root.data === "object" && !Array.isArray(root.data)
      ? (root.data as Record<string, unknown>)
      : null;
  const dataTicket =
    data?.ticket && typeof data.ticket === "object" && !Array.isArray(data.ticket)
      ? (data.ticket as Record<string, unknown>)
      : null;

  return (
    asUuid(nested?.id) ??
    asUuid(root.ticket_id) ??
    asUuid(root.id) ??
    asUuid(dataTicket?.id) ??
    asUuid(data?.ticket_id) ??
    asUuid(data?.id) ??
    null
  );
}

function extractCreatedTicketLabel(raw: unknown, fallbackId: string | null): string | null {
  if (!raw || typeof raw !== "object") return fallbackId;
  const root = raw as Record<string, unknown>;
  const nested =
    root.ticket && typeof root.ticket === "object" && !Array.isArray(root.ticket)
      ? (root.ticket as Record<string, unknown>)
      : null;
  const task =
    root.pts_task && typeof root.pts_task === "object" && !Array.isArray(root.pts_task)
      ? (root.pts_task as Record<string, unknown>)
      : root.task && typeof root.task === "object" && !Array.isArray(root.task)
        ? (root.task as Record<string, unknown>)
        : null;
  const label =
    String(nested?.ui_id ?? "").trim() ||
    String(task?.servicedesk_uid ?? "").trim() ||
    String(root.ticket_number ?? "").trim() ||
    String(root.name ?? "").trim() ||
    fallbackId ||
    "";
  return label || null;
}

function looksLikeViewImage(name: string, mime: string): boolean {
  if (mime.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|avif)$/i.test(name);
}

function looksLikeViewVideo(name: string, mime: string): boolean {
  if (mime.startsWith("video/")) return true;
  return /\.(webm|mp4|ogg|mov|avi)$/i.test(name);
}

function looksLikeViewPdf(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("pdf")) return true;
  return /\.pdf$/i.test(name);
}

function looksLikeViewDoc(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("msword") || m.includes("wordprocessingml")) return true;
  return /\.docx?$/i.test(name);
}

function looksLikeViewZip(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("zip")) return true;
  return /\.zip$/i.test(name);
}

function pdfObjectUrlFromBlob(blob: Blob): string {
  const mime = (blob.type || "").toLowerCase();
  const pdfBlob =
    mime === "application/pdf" || mime.includes("pdf")
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  return URL.createObjectURL(pdfBlob);
}

function attachedDocumentKind(name: string): { label: string; color: string } {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return { label: "PDF", color: "#dc2626" };
  if (ext === "doc" || ext === "docx") return { label: "DOC", color: "#2563eb" };
  if (ext === "zip") return { label: "ZIP", color: "#d97706" };
  return { label: "FILE", color: "currentColor" };
}

function formatAttachmentSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function attachmentKindLabel(
  name: string,
  mime: string
): "Image" | "Video" | "Pdf" | "Doc" | "Zip" | "File" {
  if (looksLikeViewImage(name, mime)) return "Image";
  if (looksLikeViewVideo(name, mime)) return "Video";
  if (looksLikeViewPdf(name, mime)) return "Pdf";
  if (looksLikeViewDoc(name, mime)) return "Doc";
  if (looksLikeViewZip(name, mime)) return "Zip";
  return "File";
}

export interface PortalTicketCreateFlowProps {
  onClose: () => void;
  onSuccess?: (
    ticketRef: string | null,
    meta?: { subject?: string; uiId?: string | null; crNumber?: string | null }
  ) => void;
  /** When true, panel fills parent dialog instead of fixed widget positioning. */
  embedded?: boolean;
  /** Skip requester Formily step — open ticket form directly (Super Agent). */
  skipRequesterStep?: boolean;
  /** Match Super Agent / ServiceDesk panel styling instead of orange widget theme. */
  agentTheme?: boolean;
  /** Close immediately after create — no "Ticket Submitted" screen (admin / super agent). */
  skipSuccessScreen?: boolean;
  /** When set, form loads ticket data and saves via update instead of create. */
  editTicketId?: string | null;
}

export function PortalTicketCreateFlow({
  onClose,
  onSuccess,
  embedded = false,
  skipRequesterStep = false,
  agentTheme = false,
  skipSuccessScreen = false,
  editTicketId = null,
}: PortalTicketCreateFlowProps) {
  const { role } = useAuth();
  const isAgentOnlyRole = isAgentShellRole(role);
  const isExternalUser = isExternalSdRole(role);
  const isExternalAgentRole = role === SD_EXTERNAL_AGENT_ROLE;
  const isExactAgentRole = role === "ServiceDesk_Agent";
  const externalUserId = isExternalUser ? getServiceDeskUserId() : null;
  const agentEmpId = isExactAgentRole ? getServiceDeskUserId() : null;
  /** Admin + Super Agent only — Agent panel create/edit must not send these fields. */
  const canIncludeChangeRequirement =
    !isAgentOnlyRole &&
    (role === "ServiceDesk_Admin" || role === "ServiceDesk_SuperAgent");
  const isEditMode = Boolean(editTicketId);
  type FlowStep = "requester" | "details" | "confirmed";
  const initialStep: FlowStep = skipRequesterStep ? "details" : "requester";
  const [step, setStep] = useState<FlowStep>(initialStep);
  const [submitting, setSubmitting] = useState(false);
  const [enduserRecordId, setEnduserRecordId] = useState<string | null>(null);
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(
    null
  );
  const [formKey, setFormKey] = useState(0);
  // Multi-image: array of { dataUrl, name }
  const [capturedScreenshots, setCapturedScreenshots] = useState<
    { dataUrl: string; name: string }[]
  >([]);
  const [isCapturing, setIsCapturing] = useState(false);

  // Screen recording state
  const [recordingState, setRecordingState] = useState<"idle" | "recording">(
    "idle"
  );
  const [recordingHandle, setRecordingHandle] =
    useState<RecordingHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
    null
  );
  const [recordedVideos, setRecordedVideos] = useState<
    { blob: Blob; name: string }[]
  >([]);
  const recordedVideoPreviewUrls = useMemo(() => {
    return recordedVideos.map(v => URL.createObjectURL(v.blob));
  }, [recordedVideos]);
  useEffect(() => {
    return () => {
      recordedVideoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [recordedVideoPreviewUrls]);
  const [attachedDocuments, setAttachedDocuments] = useState<
    { blob: Blob; name: string }[]
  >([]);
  const attachedPdfPreviewUrls = useMemo(() => {
    return attachedDocuments.map(d =>
      looksLikeViewPdf(d.name, d.blob.type)
        ? pdfObjectUrlFromBlob(d.blob)
        : null
    );
  }, [attachedDocuments]);
  useEffect(() => {
    return () => {
      attachedPdfPreviewUrls.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [attachedPdfPreviewUrls]);
  const [localPdfPreview, setLocalPdfPreview] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [previewSavedAttachment, setPreviewSavedAttachment] =
    useState<Attachment | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);

  // Manual form state for ticket fields
  const [ticketData, setTicketData] = useState({
    subject: "",
    type: "",
    category: "",
    priority: "",
    description: "",
  });
  const [ticketErrors, setTicketErrors] = useState<Record<string, string>>({});
  const [shouldLoadPriorities, setShouldLoadPriorities] = useState(false);
  const [includePtsFields, setIncludePtsFields] = useState(false);
  const [includeChangeRequirement, setIncludeChangeRequirement] =
    useState(false);
  const [changeReqData, setChangeReqData] = useState({
    ...EMPTY_CHANGE_REQ_DATA,
  });
  const [changeReqErrors, setChangeReqErrors] = useState<Record<string, string>>(
    {}
  );

  // PTS form state
  const [ptsData, setPtsData] = useState({
    project: "",
    task: "",
    estimationHours: "",
    workType: "",
  });
  const [ptsErrors, setPtsErrors] = useState<Record<string, string>>({});
  const [openPtsSelectId, setOpenPtsSelectId] = useState<string | null>(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const showCopyTicketButton =
    agentTheme && skipRequesterStep && !isEditMode;
  const [existingAttachmentIds, setExistingAttachmentIds] = useState<string[]>(
    []
  );
  const [existingInlineAttachments, setExistingInlineAttachments] = useState<
    Attachment[]
  >([]);
  const { attachments: fetchedAttachments } = useAttachmentFilesByIds(
    isEditMode ? existingAttachmentIds : undefined
  );
  const editAttachments = useMemo(() => {
    const byId = new Map<string, Attachment>();
    existingInlineAttachments.forEach(item => byId.set(item.id, item));
    fetchedAttachments.forEach(item => byId.set(item.id, item));
    return [...byId.values()];
  }, [existingInlineAttachments, fetchedAttachments]);

  const enduserForm = useMemo(() => createForm({}), [formKey]);

  // Load "ServiceDesk Enduser" schema for step 1
  const {
    data: enduserSchema,
    isLoading: enduserLoading,
    isError: enduserSchemaError,
    refetch: refetchEnduserSchema,
  } = useQuery({
    queryKey: ["schema", "ServiceDesk Enduser"],
    queryFn: async () => {
      const schema = await ServiceDeskAPIService.schema.getByName(
        "ServiceDesk Enduser"
      );
      if (!schema) {
        throw new Error("Schema not found");
      }
      return schema;
    },
    enabled: step === "requester",
    retry: 1,
  });

  // Load priorities from API (only when dropdown is clicked)
  const { data: prioritiesData, isLoading: prioritiesLoading } = useQuery({
    queryKey: ["service-desk", "priorities"],
    queryFn: async () => {
      const response = await ServiceDeskAPIService.priorities.list();
      return response.data;
    },
    enabled: shouldLoadPriorities || (step === "details" && includePtsFields),
    retry: 1,
  });

  const ptsProjectSelectReady =
    step === "details" &&
    (isExternalUser || includePtsFields || Boolean(editTicketId));

  const {
    data: assignedProjectsData,
    isLoading: assignedProjectsLoading,
    isError: assignedProjectsError,
  } = useQuery({
    queryKey: serviceDeskUserProjectQueryKey(externalUserId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.userProjects.list(
        externalUserId ?? undefined
      );
      return response.data;
    },
    enabled: Boolean(externalUserId) && ptsProjectSelectReady,
    staleTime: 15_000,
    retry: 1,
  });

  const {
    data: agentAssignedProjectsData,
    isLoading: agentAssignedProjectsLoading,
    isError: agentAssignedProjectsError,
  } = useQuery({
    queryKey: serviceDeskAgentAssignedProjectsQueryKey(agentEmpId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.agentAssignedProjects.list(
        agentEmpId ?? undefined
      );
      return response.data;
    },
    enabled: Boolean(agentEmpId) && ptsProjectSelectReady,
    staleTime: 15_000,
    retry: 1,
  });

  // Load open projects for PTS Select Project dropdown
  const {
    data: ptsProjectsData,
    isLoading: ptsOpenProjectsLoading,
    isError: ptsOpenProjectsError,
  } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled: !isExternalUser && !isExactAgentRole && ptsProjectSelectReady,
    retry: 1,
  });

  const ptsProjectsLoading = isExternalUser
    ? assignedProjectsLoading
    : isExactAgentRole
      ? agentAssignedProjectsLoading
      : ptsOpenProjectsLoading;
  const ptsProjectsError = isExternalUser
    ? assignedProjectsError
    : isExactAgentRole
      ? agentAssignedProjectsError
      : ptsOpenProjectsError;

  const PTS_PROJECTS = useMemo(
    () =>
      ptsDropdownOptionsFromPayload(
        isExternalUser
          ? assignedProjectsData
          : isExactAgentRole
            ? agentAssignedProjectsData
            : ptsProjectsData
      ),
    [
      agentAssignedProjectsData,
      assignedProjectsData,
      isExactAgentRole,
      isExternalUser,
      ptsProjectsData,
    ]
  );

  // Load tasks for selected project
  const {
    data: ptsTasksData,
    isLoading: ptsTasksLoading,
    isError: ptsTasksError,
  } = useQuery({
    queryKey: ptsTaskListQueryKey(ptsData.project),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.taskList(
        ptsData.project
      );
      return response.data;
    },
    enabled:
      !isExternalUser &&
      step === "details" &&
      (includePtsFields || Boolean(editTicketId)) &&
      !!ptsData.project,
    retry: 1,
  });

  const PTS_TASKS = useMemo(
    () => ptsTaskOptionsFromPayload(ptsTasksData),
    [ptsTasksData]
  );

  // Load Work Type options
  const { data: ptsWorkTypeData, isLoading: ptsWorkTypeLoading } = useQuery({
    queryKey: ptsWorkTypeListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.workTypeList();
      return response.data;
    },
    enabled:
      !isExternalUser &&
      step === "details" &&
      (includePtsFields || Boolean(editTicketId)),
    retry: 1,
  });

  const PTS_WORK_TYPES = useMemo(
    () => ptsWorkTypeOptionsFromPayload(ptsWorkTypeData),
    [ptsWorkTypeData]
  );

  const PTS_PROJECT_OPTIONS = useMemo(
    () =>
      ensurePtsSelectedOption(PTS_PROJECTS, ptsData.project, "Saved project"),
    [PTS_PROJECTS, ptsData.project]
  );

  const PTS_TASK_OPTIONS = useMemo(
    () => ensurePtsSelectedOption(PTS_TASKS, ptsData.task, "Saved task"),
    [PTS_TASKS, ptsData.task]
  );

  const PTS_WORK_TYPE_OPTIONS = useMemo(
    () =>
      ensurePtsSelectedOption(
        PTS_WORK_TYPES,
        ptsData.workType,
        "Saved work type"
      ),
    [PTS_WORK_TYPES, ptsData.workType]
  );

  const { data: changeReqEmployeesData, isLoading: changeReqEmployeesLoading } =
    useQuery({
      queryKey: serviceDeskEmployeeListQueryKey,
      queryFn: async () => {
        const response = await ServiceDeskAPIService.lists.employees();
        return response.data;
      },
      enabled:
        canIncludeChangeRequirement &&
        step === "details" &&
        (includePtsFields || Boolean(editTicketId)),
      staleTime: 5 * 60 * 1000,
      retry: 1,
    });

  const CHANGE_REQ_EMPLOYEE_OPTIONS = useMemo(() => {
    const base = employeeListAssignSelectOptions(changeReqEmployeesData);
    return changeReqData.assignedEmployee
      .split(",")
      .map(part => part.trim())
      .filter(Boolean)
      .reduce(
        (opts, id) => ensurePtsSelectedOption(opts, id, "Selected employee"),
        base
      );
  }, [changeReqEmployeesData, changeReqData.assignedEmployee]);

  const resetChangeRequirementFields = () => {
    setIncludeChangeRequirement(false);
    setChangeReqData({ ...EMPTY_CHANGE_REQ_DATA });
    setChangeReqErrors({});
  };

  // Transform priorities API data to dropdown format
  const PRIORITIES = useMemo(() => {
    if (!prioritiesData) return [];

    // Handle different response formats
    let dataArray = prioritiesData;
    if (prioritiesData.priorities) dataArray = prioritiesData.priorities;
    else if (prioritiesData.results) dataArray = prioritiesData.results;
    else if (prioritiesData.data) dataArray = prioritiesData.data;

    if (!Array.isArray(dataArray) || dataArray.length === 0) return [];

    return dataArray.map((p: any) => ({
      value: p.id || p.value || String(p),
      label: formatPriorityLabel(
        p.priority || p.label || p.name || p.value || String(p)
      ),
    }));
  }, [prioritiesData]);

  const PRIORITY_OPTIONS = useMemo(
    () =>
      ensurePtsSelectedOption(
        PRIORITIES,
        ticketData.priority,
        "Saved priority"
      ),
    [PRIORITIES, ticketData.priority]
  );

  // Deduplicate schema properties + fix layout for widget rendering
  const deduplicatedEnduserSchema = useMemo(() => {
    const raw = enduserSchema?.json ?? enduserSchema;
    if (!raw) return null;

    let parsed: Record<string, unknown>;
    if (typeof raw === "string") {
      try {
        parsed = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        return raw;
      }
    } else if (typeof raw === "object" && raw !== null) {
      parsed = raw as Record<string, unknown>;
    } else {
      return raw;
    }

    const schema = (parsed.schema ?? parsed) as Record<string, unknown>;
    if (!schema?.properties || typeof schema.properties !== "object") {
      return parsed;
    }

    const seen = new Set<string>();
    const dedupedProps: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(
      schema.properties as Record<string, unknown>
    )) {
      if (!seen.has(key)) {
        seen.add(key);
        const field = val as Record<string, unknown>;
        const decoratorProps = (field["x-decorator-props"] ?? {}) as Record<
          string,
          unknown
        >;
        const { layout: _layout, ...cleanDecoratorProps } = decoratorProps;
        dedupedProps[key] = {
          ...field,
          "x-decorator-props": cleanDecoratorProps,
        };
      }
    }
    return {
      ...parsed,
      form: { layout: "vertical" },
      schema: { ...schema, properties: dedupedProps },
    };
  }, [enduserSchema]);

  const panelClassName = (extra?: string) =>
    cn(
      "sd-panel sd-panel--expanded",
      embedded && "sd-panel--embedded",
      agentTheme && "sd-panel--agent-theme",
      extra
    );

  const accentColor = agentTheme ? "#1e293b" : "#f97316";
  const accentLightBg = agentTheme ? "#f1f5f9" : "#fff7ed";
  const uiTheme = agentTheme ? SD_AGENT_THEME : SD_WIDGET_THEME;
  const panelTitle = isEditMode
    ? "ServiceDesk · Edit Ticket"
    : agentTheme
      ? "ServiceDesk · New Ticket"
      : "SYSTEMDESK · NEW TICKET";
  const headline = isEditMode
    ? "Edit Ticket"
    : agentTheme
      ? "New Ticket"
      : "Create a Ticket";
  const panelSub = isEditMode
    ? "Update the ticket details below"
    : agentTheme
      ? "Fill in the details below to create a support ticket"
      : "Describe your issue and we'll get right on it";
  const submitLabel = isEditMode
    ? "Save Changes"
    : agentTheme
      ? "Create Ticket"
      : "Send report";

  useEffect(() => {
    if (!editTicketId) return;
    let cancelled = false;
    setLoadingTicket(true);
    setShouldLoadPriorities(true);
    void ServiceDeskAPIService.tickets
      .getCreatedTicket(editTicketId)
      .then(async response => {
        if (cancelled || !response?.data) return;
        const rawPayload = (response.data ?? {}) as Record<string, unknown>;
        const r = decodeHtmlEntitiesInRecord(
          normalizeTicketCreateDetailPayload(rawPayload)
        );
        const mappedType = mapFormTypeFromTicketRecord(r);
        setTicketData({
          subject: String(r.subject ?? "").trim(),
          type: mappedType,
          category: normalizeCategoryFilterValue(String(r.category ?? "")),
          priority: String(r.priority_id ?? "").trim(),
          description: plainTextToRichHtml(String(r.description ?? "").trim()),
        });
        const { ids, previews } = parseTicketAttachmentsFromRecord(r);
        setExistingAttachmentIds(ids);
        setExistingInlineAttachments(previews);
        const { includePts, ptsData: loadedPts } = parsePtsFromTicketRecord(r);
        let loadedChangeReq = canIncludeChangeRequirement
          ? mergeParsedChangeRequest(
              parseChangeRequestFromTicketRecord(r),
              parseChangeRequestFromTicketRecord(rawPayload)
            )
          : null;
        if (
          loadedChangeReq &&
          (!loadedChangeReq.changeReqData.requestedDate ||
            !loadedChangeReq.changeReqData.expectedCompletion ||
            !loadedChangeReq.changeReqData.assignedEmployee)
        ) {
          const crId = String(
            findChangeRequestObject(r)?.id ??
              findChangeRequestObject(rawPayload)?.id ??
              ""
          ).trim();
          if (crId) {
            try {
              const crRes = await ServiceDeskAPIService.formily.getRecord(
                "Change Request",
                crId,
                { skipErrorNotification: true }
              );
              loadedChangeReq = mergeParsedChangeRequest(
                loadedChangeReq,
                parseChangeRequestFromTicketRecord(
                  decodeHtmlEntitiesInRecord(
                    normalizeTicketCreateDetailPayload(crRes.data)
                  )
                )
              );
            } catch {
              // Ticket GET often has only a CR id, not the nested object.
            }
          }
        }
        setIncludePtsFields(
          includePts || Boolean(loadedChangeReq?.includeChangeRequirement)
        );
        if (includePts) {
          setPtsData(loadedPts);
        }
        if (loadedChangeReq?.includeChangeRequirement) {
          setIncludeChangeRequirement(true);
          setChangeReqData(loadedChangeReq.changeReqData);
        }
      })
      .catch(() => {
        notify.error("Load failed", "Could not load ticket details.");
      })
      .finally(() => {
        if (!cancelled) setLoadingTicket(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editTicketId, canIncludeChangeRequirement]);

  const handleOpenSelectIdChange = (id: string | null) => {
    setOpenPtsSelectId(id);
    if (id === "ticket-priority") setShouldLoadPriorities(true);
  };

  const closeWidget = () => {
    setFormKey(k => k + 1);
    setEnduserRecordId(null);
    setTicketData({
      subject: "",
      type: "",
      category: "",
      priority: "",
      description: "",
    });
    setPtsData({ project: "", task: "", estimationHours: "", workType: "" });
    setPtsErrors({});
    setOpenPtsSelectId(null);
    setIncludePtsFields(false);
    setIncludeChangeRequirement(false);
    setChangeReqData({ ...EMPTY_CHANGE_REQ_DATA });
    setChangeReqErrors({});
    setCapturedScreenshots([]);
    setAttachedDocuments([]);
    setExistingAttachmentIds([]);
    setExistingInlineAttachments([]);
    // Stop any active recording
    if (recordingState === "recording") recordingHandle?.stop();
    setRecordedVideos([]);
    setRecordingState("idle");
    setRecordingHandle(null);
    setRecordingStream(null);
    setStep(initialStep);
    onClose();
  };

  // Helper: add a dataUrl to the screenshots array
  const addScreenshot = (dataUrl: string, name: string) => {
    setCapturedScreenshots(prev => [...prev, { dataUrl, name }]);
  };

  // Remove one screenshot by index
  const handleRemoveScreenshot = (idx: number) => {
    setCapturedScreenshots(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveExistingAttachment = (attachmentId: string) => {
    setExistingAttachmentIds(prev => prev.filter(id => id !== attachmentId));
    setExistingInlineAttachments(prev =>
      prev.filter(item => item.id !== attachmentId)
    );
  };

  // Capture ANY window/screen
  const handleCaptureSystemScreen = async () => {
    setIsCapturing(true);
    try {
      notify.info("Screen Capture", "🎬 Select any window and click 'Share'");
      const result = await captureScreen(4 * 1024 * 1024);
      if (result.ok) {
        addScreenshot(result.dataUrl, `window_${Date.now()}.png`);
        notify.success(
          "✅ Captured!",
          "Click back to ServiceDesk tab to continue"
        );
      } else {
        notify.warning(
          "Cancelled",
          result.reason || "Screen sharing cancelled."
        );
      }
    } catch (e) {
      notify.error(
        "Error",
        e instanceof Error ? e.message : "Failed to capture screen"
      );
    } finally {
      setIsCapturing(false);
    }
  };

  // Capture current page
  const handleCaptureCurrentPage = async () => {
    try {
      const result = await captureViewport(
        document.body,
        false,
        4 * 1024 * 1024
      );
      if (result.ok) {
        addScreenshot(result.dataUrl, `page_${Date.now()}.png`);
      } else {
        notify.error("Error", result.reason || "Failed to capture page");
      }
    } catch (e) {
      notify.error("Error", e instanceof Error ? e.message : "Capture failed");
    }
  };

  // ─── Recording helpers ────────────────────────────────────────────────────
  const formatRecordingTime = (s: number) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const handleStartRecording = async () => {
    const result = await startScreenRecording((blob, name) => {
      setRecordedVideos(prev => [...prev, { blob, name }]);
      setRecordingState("idle");
      setRecordingHandle(null);
      setRecordingStream(null);
      notify.success("Recording saved!", "Video attached to your ticket.");
    });
    if (!result.ok) {
      notify.warning("Cancelled", result.reason);
      return;
    }
    setRecordingHandle(result.handle);
    setRecordingStream(result.stream);
    setRecordingState("recording");
    notify.info(
      "Recording started!",
      "Screen recording active · Max 5 min · Stop anytime"
    );
  };

  const handleStopRecording = () => {
    recordingHandle?.stop();
  };

  const handleRemoveVideo = (idx: number) => {
    setRecordedVideos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveDocument = (idx: number) => {
    setAttachedDocuments(prev => prev.filter((_, i) => i !== idx));
  };

  /** Opens a not-yet-uploaded PDF in the in-app preview overlay. */
  const handlePreviewDocument = (doc: { blob: Blob; name: string }) => {
    if (!looksLikeViewPdf(doc.name, doc.blob.type)) {
      return;
    }
    setPreviewSavedAttachment(null);
    const mime = (doc.blob.type || "").toLowerCase();
    const pdfBlob =
      mime === "application/pdf" || mime.includes("pdf")
        ? doc.blob
        : new Blob([doc.blob], { type: "application/pdf" });
    setLocalPdfPreview(prev => {
      if (prev?.url) {
        URL.revokeObjectURL(prev.url);
      }
      return { url: URL.createObjectURL(pdfBlob), name: doc.name };
    });
  };

  const closeLocalPdfPreview = () => {
    setLocalPdfPreview(prev => {
      if (prev?.url) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
  };

  const processAttachmentFiles = (files: File[]) => {
    if (!files.length) return;

    files.forEach(file => {
      if (!isAllowedTicketAttachmentUpload(file)) {
        notify.error(
          IMAGE_UPLOAD_ERROR_TITLE,
          ticketAttachmentRejectedMessage(file)
        );
        return;
      }

      if (isAllowedVideoUpload(file)) {
        if (file.size > TICKET_ATTACHMENT_VIDEO_MAX_BYTES) {
          notify.error(
            "File too large",
            `${file.name} exceeds ${TICKET_ATTACHMENT_VIDEO_MAX_MB} MB`
          );
          return;
        }
        setRecordedVideos(prev => [...prev, { blob: file, name: file.name }]);
        return;
      }

      if (isAllowedDocumentUpload(file)) {
        if (file.size > TICKET_ATTACHMENT_DOCUMENT_MAX_BYTES) {
          notify.error(
            "File too large",
            `${file.name} exceeds 10 MB`
          );
          return;
        }
        setAttachedDocuments(prev => [...prev, { blob: file, name: file.name }]);
        return;
      }

      if (file.size > TICKET_ATTACHMENT_IMAGE_MAX_BYTES) {
        notify.error("File too large", `${file.name} exceeds 5 MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        if (!isImageDataUrl(dataUrl)) {
          notify.error(
            IMAGE_UPLOAD_ERROR_TITLE,
            imageUploadRejectedMessage(file.name)
          );
          return;
        }
        addScreenshot(dataUrl, file.name);
      };
      reader.readAsDataURL(file);
    });
  };

  const openAttachmentPicker = async () => {
    if (submitting || isCapturing) return;
    const files = await pickTicketAttachmentUploadFiles(true, fileInputRef.current);
    processAttachmentFiles(files);
  };

  const handleAttachAreaClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    void openAttachmentPicker();
  };

  // Attach live stream to video preview element
  useEffect(() => {
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = recordingStream;
    }
  }, [recordingStream]);

  // Recording timer
  useEffect(() => {
    if (recordingState !== "recording") return;
    setRecordingSeconds(0);
    const interval = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [recordingState]);

  // Auto-stop at 5 minutes (300 seconds)
  useEffect(() => {
    if (recordingState === "recording" && recordingSeconds >= 300) {
      notify.warning(
        "Time limit reached",
        "Max 5 minute recording reached — video saved!"
      );
      recordingHandle?.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingSeconds]);

  // Clipboard paste (Ctrl+V) — screenshots only; never hijack Subject / text fields
  useEffect(() => {
    if (step !== "details") return;
    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === "INPUT") return;

      const plainText = e.clipboardData?.getData("text/plain")?.trim() ?? "";
      if (target?.tagName === "TEXTAREA" && plainText.length > 0) return;

      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (!file) continue;
          if (!isAllowedImageUpload(file)) {
            notify.error(
              IMAGE_UPLOAD_ERROR_TITLE,
              imageUploadRejectedMessage(file.name)
            );
            return;
          }
          if (file.size > TICKET_ATTACHMENT_IMAGE_MAX_BYTES) {
            notify.error("File too large", "Max 5MB");
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            addScreenshot(reader.result as string, `paste_${Date.now()}.png`);
            notify.success("Pasted", "✅ Screenshot pasted from clipboard!");
          };
          reader.readAsDataURL(file);
          e.preventDefault();
          break;
        }
      }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [step]);

  // Step 1: Submit enduser details
  const handleRequesterSubmit = async () => {
    try {
      await enduserForm.validate();
    } catch {
      notify.error("Validation", "Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      let values = removeMetaFields(enduserForm.values) as Record<
        string,
        unknown
      >;
      if (enduserSchema) {
        values = normalizeDynamicModuleUploadFields(
          enduserSchema?.json ?? enduserSchema,
          values
        );
      }
      const response = await ServiceDeskAPIService.formily.createItem(
        "ServiceDesk Enduser",
        values
      );
      const recordId = response?.id ?? response?.data?.id ?? null;
      setEnduserRecordId(recordId);
      setStep("details");
    } catch (e) {
      notify.error(
        "Submit failed",
        e instanceof Error ? e.message : "Could not save details."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: Convert data URL to File
  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleSubmit = async (opts?: { keepFormOpen?: boolean }) => {
    const keepFormOpen = Boolean(opts?.keepFormOpen) && !isEditMode;
    const ticketErrs: Record<string, string> = {};
    const subjectTrimmed = ticketData.subject.trim();
    const descriptionPlain = richTextPlainText(ticketData.description);

    if (!subjectTrimmed) ticketErrs.subject = "Subject is required.";
    else if (subjectTrimmed.length > SUBJECT_MAX_LENGTH) {
      ticketErrs.subject = subjectMaxLengthError();
    }
    if (!ticketData.type) ticketErrs.type = "Please select a ticket type.";
    if (!ticketData.category) ticketErrs.category = "Please select a category.";
    if (!ticketData.priority) ticketErrs.priority = "Please select a priority.";
    if (!descriptionPlain)
      ticketErrs.description = "Description is required.";
    else if (descriptionPlain.length < 10) {
      ticketErrs.description = "Description must be at least 10 characters.";
    } else if (descriptionPlain.length > DESCRIPTION_MAX_LENGTH) {
      ticketErrs.description = `Description must be at most ${DESCRIPTION_MAX_LENGTH} characters.`;
    }

    const ptsErrs: Record<string, string> = {};
    let estimationHours = 0;

    if (isExternalUser) {
      if (!ptsData.project) ptsErrs.project = "Please select a project.";
    } else if (includePtsFields) {
      if (!ptsData.project) ptsErrs.project = "Please select a project.";
      if (!ptsData.task) ptsErrs.task = "Please select a task.";

      estimationHours = parseInt(ptsData.estimationHours, 10);
      if (!ptsData.estimationHours) {
        ptsErrs.estimationHours = "Estimation Hours is required.";
      } else if (isNaN(estimationHours) || estimationHours < 1) {
        ptsErrs.estimationHours = "Minimum 1 hour required.";
      } else if (estimationHours > 999) {
        ptsErrs.estimationHours = "Cannot exceed 999 hours.";
      }

      if (!ptsData.workType) ptsErrs.workType = "Please select a work type.";
    }

    setTicketErrors(ticketErrs);
    setPtsErrors(ptsErrs);

    const changeReqErrs: Record<string, string> = {};
    if (canIncludeChangeRequirement && includePtsFields && includeChangeRequirement) {
      if (!changeReqData.requestedDate) {
        changeReqErrs.requestedDate = "Requested date is required.";
      }
      if (!changeReqData.expectedCompletion) {
        changeReqErrs.expectedCompletion = "Expected completion is required.";
      }
      if (
        changeReqData.requestedDate &&
        changeReqData.expectedCompletion &&
        changeReqData.expectedCompletion < changeReqData.requestedDate
      ) {
        changeReqErrs.expectedCompletion =
          "Expected completion cannot be before requested date.";
      }
      if (!changeReqData.assignedEmployee) {
        changeReqErrs.assignedEmployee = "Please select at least one employee.";
      }
    }
    setChangeReqErrors(changeReqErrs);

    if (
      Object.keys(ticketErrs).length > 0 ||
      Object.keys(ptsErrs).length > 0 ||
      Object.keys(changeReqErrs).length > 0
    )
      return;

    const appendPtsPayload = (formData: FormData) => {
      if (isExternalUser) {
        formData.append("is_pts", "true");
        formData.append("project", ptsData.project);
        formData.append("is_external", "true");
        return;
      }
      formData.append("is_pts", includePtsFields ? "true" : "false");
      if (includePtsFields) {
        formData.append("project", ptsData.project);
        formData.append("taskestimatedhours", String(estimationHours));
        formData.append("parent_task", ptsData.task);
        formData.append("pts_status", PTS_PORTAL_SUBTASK_CREATE_STATUS_ID);
        formData.append("work_type", ptsData.workType);
      }
    };

    if (editTicketId) {
      setSubmitting(true);
      try {
        const currentResponse =
          await ServiceDeskAPIService.tickets.getCreatedTicket(editTicketId);
        if (!currentResponse?.data) {
          notify.error("Update failed", "Ticket not found.");
          return;
        }
        const base = decodeHtmlEntitiesInRecord(
          normalizeTicketCreateDetailPayload(currentResponse.data)
        );

        const requesterId =
          String(base.requester ?? "").trim() || getServiceDeskUserId();
        if (!requesterId) {
          notify.error(
            "Update failed",
            "Could not resolve requester for this ticket."
          );
          return;
        }

        const formData = new FormData();
        formData.append("folder_name", "uploads");

        if (capturedScreenshots.length > 0) {
          const invalid = capturedScreenshots.filter(
            ss => !isImageDataUrl(ss.dataUrl)
          );
          if (invalid.length > 0) {
            notify.error(
              IMAGE_UPLOAD_ERROR_TITLE,
              IMAGE_UPLOAD_VALIDATION_MESSAGE
            );
            return;
          }
          const files = capturedScreenshots.map((ss, i) =>
            dataURLtoFile(ss.dataUrl, ss.name || `image_${i}.png`)
          );
          const rejected = files.find(f => !isAllowedImageUpload(f));
          if (rejected) {
            notify.error(
              IMAGE_UPLOAD_ERROR_TITLE,
              IMAGE_UPLOAD_VALIDATION_MESSAGE
            );
            return;
          }
          files.forEach(file => formData.append("file_path", file));
        }

        if (recordedVideos.length > 0) {
          recordedVideos.forEach(v => {
            const file = new File([v.blob], v.name, {
              type: v.blob.type || "video/webm",
            });
            formData.append("file_path", file);
          });
        }

        if (attachedDocuments.length > 0) {
          attachedDocuments.forEach(d => {
            const file = new File([d.blob], d.name, {
              type: d.blob.type || mimeFromAttachmentFilename(d.name),
            });
            formData.append("file_path", file);
          });
        }

        formData.append(
          "attachments",
          JSON.stringify(existingAttachmentIds)
        );

        formData.append("category", ticketData.category);
        formData.append("description", sanitizeRichTextHtml(ticketData.description));
        formData.append("is_vip", String(base.is_vip ?? false));
        formData.append("priority_id", ticketData.priority);
        formData.append("requester", requesterId);
        formData.append("sla", String(base.sla ?? "on Track"));
        formData.append("status", String(base.status ?? "new"));
        formData.append("subject", sanitizeInput(subjectTrimmed));
        formData.append("tickettype", ticketTypeForApi(
          ticketData.type,
          String(base.tickettype ?? "issue")
        ));
        appendPtsPayload(formData);

        if (canIncludeChangeRequirement) {
          appendChangeRequestFormFields(formData, {
            enabled: Boolean(includePtsFields && includeChangeRequirement),
            requestedDate: changeReqData.requestedDate,
            expectedCompletion: changeReqData.expectedCompletion,
            assignedEmployees: changeReqData.assignedEmployee,
          });
        }

        await ServiceDeskAPIService.tickets.updateCreatedTicket(
          editTicketId,
          formData
        );
        onSuccess?.(editTicketId);
        onClose();
      } catch (e: unknown) {
        notify.error(
          "Update failed",
          e instanceof Error ? e.message : "Could not save ticket."
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const requesterId = getServiceDeskUserId() ?? enduserRecordId;
    if (!requesterId) {
      notify.error(
        "Requester missing",
        skipRequesterStep
          ? "Could not resolve your user id. Please sign in again."
          : "Please sign in or complete your details in step 1."
      );
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("folder_name", "uploads");

      if (capturedScreenshots.length > 0) {
        const invalid = capturedScreenshots.filter(
          ss => !isImageDataUrl(ss.dataUrl)
        );
        if (invalid.length > 0) {
          notify.error(
            IMAGE_UPLOAD_ERROR_TITLE,
            IMAGE_UPLOAD_VALIDATION_MESSAGE
          );
          setSubmitting(false);
          return;
        }
        const files = capturedScreenshots.map((ss, i) =>
          dataURLtoFile(ss.dataUrl, ss.name || `image_${i}.png`)
        );
        const rejected = files.find(f => !isAllowedImageUpload(f));
        if (rejected) {
          notify.error(
            IMAGE_UPLOAD_ERROR_TITLE,
            IMAGE_UPLOAD_VALIDATION_MESSAGE
          );
          setSubmitting(false);
          return;
        }
        files.forEach(file => formData.append("file_path", file));
      }

      if (recordedVideos.length > 0) {
        recordedVideos.forEach(v => {
          const file = new File([v.blob], v.name, {
            type: v.blob.type || "video/webm",
          });
          formData.append("file_path", file);
        });
      }

      if (attachedDocuments.length > 0) {
        attachedDocuments.forEach(d => {
          const file = new File([d.blob], d.name, {
            type: d.blob.type || mimeFromAttachmentFilename(d.name),
          });
          formData.append("file_path", file);
        });
      }

      formData.append("category", ticketData.category);
      formData.append("description", sanitizeRichTextHtml(ticketData.description));
      formData.append("is_vip", "false");
      formData.append("priority_id", ticketData.priority);
      formData.append("requester", requesterId);
      formData.append("sla", "on Track");
      formData.append("status", "new");
      formData.append("subject", sanitizeInput(subjectTrimmed));
      formData.append("tickettype", ticketTypeForApi(ticketData.type));
      appendPtsPayload(formData);

      if (canIncludeChangeRequirement) {
        appendChangeRequestFormFields(formData, {
          enabled: Boolean(includePtsFields && includeChangeRequirement),
          requestedDate: changeReqData.requestedDate,
          expectedCompletion: changeReqData.expectedCompletion,
          assignedEmployees: changeReqData.assignedEmployee,
        });
      }

      const response =
        await ServiceDeskAPIService.tickets.createWithFile(formData);
      const payload = response.data;

      const createdId = extractCreatedTicketId(payload);
      const createdLabel = extractCreatedTicketLabel(payload, createdId);
      const createdRef = createdId ?? createdLabel;
      const createdCrNumber = extractCreatedChangeRequestNumber(payload);

      // Copy Ticket: create now, keep form filled so user can tweak & create again.
      if (keepFormOpen) {
        setCapturedScreenshots([]);
        setRecordedVideos([]);
        setAttachedDocuments([]);
        setTicketErrors({});
        onSuccess?.(createdRef, {
          subject: subjectTrimmed,
          uiId: createdLabel,
          crNumber: createdCrNumber,
        });
        notify.success(
          createdLabel
            ? `Created ${createdLabel}`
            : "Ticket created",
          createdCrNumber
            ? `Change request ${createdCrNumber}. Form kept filled — change what you need, then Create Ticket for another copy.`
            : "Form kept filled — change what you need, then Create Ticket for another copy."
        );
        return;
      }

      setTicketData({
        subject: "",
        type: "",
        category: "",
        priority: "",
        description: "",
      });
      setTicketErrors({});
      setPtsData({ project: "", task: "", estimationHours: "", workType: "" });
      setPtsErrors({});
      setIncludePtsFields(false);
      setIncludeChangeRequirement(false);
      setChangeReqData({ ...EMPTY_CHANGE_REQ_DATA });
      setChangeReqErrors({});
      setCapturedScreenshots([]);
      setRecordedVideos([]);
      setAttachedDocuments([]);

      onSuccess?.(createdRef, {
        subject: subjectTrimmed,
        uiId: createdLabel,
        crNumber: createdCrNumber,
      });

      if (skipSuccessScreen) {
        onClose();
        return;
      }

      setSubmittedTicketId(createdLabel ?? createdRef);
      setStep("confirmed");
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error?.message ??
        e?.response?.data?.error ??
        (e instanceof Error ? e.message : "Could not create ticket.");
      notify.error(
        "Submit failed",
        typeof msg === "string" ? msg : "Could not create ticket."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfigProvider theme={uiTheme}>
      {/* ── Persistent recording badge — always on top, visible on any tab switch ── */}
      {recordingState === "recording" && (
        <div className="sd-rec-floating-badge" data-screenshot-skip="true">
          <span className="sd-rec-dot-sm"></span>
          <span className="sd-rec-floating-timer">
            REC {formatRecordingTime(recordingSeconds)}
          </span>
          <button
            className="sd-rec-floating-stop"
            onClick={handleStopRecording}
          >
            ■ Stop
          </button>
        </div>
      )}

      {/* Panel - Step 1: Requester */}
      {step === "requester" && (
        <div
          className={panelClassName()}
          style={embedded ? undefined : { right: "24px", left: "auto" }}
          role="dialog"
          aria-label="Support"
          aria-modal="false"
        >
          <div className="sd-panel-header">
            <div className="sd-panel-title">
              <span className="sd-dot"></span> SYSTEMDESK · SUPPORT
            </div>
            <button
              onClick={closeWidget}
              className="sd-panel-close"
              aria-label="Close"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>

          {/* Step Progress */}
          <div className="sd-step-bar">
            <div className="sd-step-item">
              <span className="sd-step-num active">1</span>
              <span className="sd-step-label active">Your Info</span>
            </div>
            <div className="sd-step-divider"></div>
            <div className="sd-step-item">
              <span className="sd-step-num">2</span>
              <span className="sd-step-label">Ticket Details</span>
            </div>
          </div>

          <div className="sd-panel-body sd-panel-body--compact">
            <h2 className="sd-panel-headline">Tell us who you are</h2>
            <p className="sd-panel-sub">
              We'll use this to track your support request
            </p>

            {enduserLoading ? (
              <div className="sd-loading">
                <LoadingSpinner />
              </div>
            ) : enduserSchema && deduplicatedEnduserSchema ? (
              <div className="sd-schema-form">
                <SchemaFormView
                  form={enduserForm}
                  schemaJson={deduplicatedEnduserSchema}
                />
              </div>
            ) : (
              <div className="sd-status sd-status-error">
                <span className="sd-status-icon">⚠</span>
                <span>Could not load form. Please try again.</span>
                {enduserSchemaError && (
                  <button
                    type="button"
                    className="sd-btn sd-btn-retry"
                    style={{ marginTop: 8 }}
                    onClick={() => void refetchEnduserSchema()}
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="sd-panel-footer">
            <div className="sd-actions">
              <button className="sd-btn" onClick={closeWidget}>
                Cancel
              </button>
              <button
                className="sd-btn sd-btn-primary"
                onClick={handleRequesterSubmit}
                disabled={submitting || enduserLoading || !enduserSchema}
              >
                {submitting ? (
                  <>
                    <span className="sd-spinner"></span> Saving…
                  </>
                ) : (
                  "Continue →"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel - Confirmed: Ticket submitted successfully */}
      {step === "confirmed" && (
        <div
          className={cn(
            "sd-panel sd-panel-confirmed",
            embedded && "sd-panel--embedded",
            agentTheme && "sd-panel--agent-theme"
          )}
          style={embedded ? undefined : { right: "24px", left: "auto" }}
          role="dialog"
          aria-label="Ticket submitted"
        >
          <div className="sd-confirmed-wrap">
            <div className="sd-confirmed-icon-ring">
              <svg viewBox="0 0 48 48" width="40" height="40" fill="none">
                <circle cx="24" cy="24" r="24" fill="#dcfce7" />
                <path
                  d="M14 25l7 7 13-13"
                  stroke="#16a34a"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="sd-confirmed-title">Ticket Submitted!</div>
            <div className="sd-confirmed-sub">
              Your issue has been logged and our support team will get back to
              you shortly.
            </div>
            {submittedTicketId && (
              <div className="sd-confirmed-id">
                Ticket ID: <strong>{submittedTicketId}</strong>
              </div>
            )}
            <button className="sd-confirmed-close-btn" onClick={closeWidget}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Panel - Step 2: Ticket Details */}
      {step === "details" && (
        <div
          className={panelClassName()}
          style={embedded ? undefined : { right: "24px", left: "auto" }}
          role="dialog"
          aria-label="Support"
          aria-modal="false"
        >
          {/* Capturing Overlay */}
          {isCapturing && (
            <div className="sd-capturing-overlay">
              <div className="sd-capturing-box">
                <div className="sd-spinner-large"></div>
                <div className="sd-capturing-text">
                  <strong>📸 Waiting for your selection...</strong>
                  <span>
                    Select window/screen in browser dialog and click "Share"
                  </span>
                  <span
                    className="sd-capturing-hint"
                    style={{ color: "#dc2626" }}
                  >
                    Browser will switch to selected window — click back to
                    ServiceDesk tab when done
                  </span>
                </div>
              </div>
            </div>
          )}

          {(localPdfPreview || previewSavedAttachment) && (
            <div
              className="sd-pdf-preview-overlay"
              role="dialog"
              aria-label="PDF preview"
              onClick={e => e.stopPropagation()}
            >
              <div className="sd-pdf-preview-overlay__bar">
                <span className="sd-pdf-preview-overlay__title">
                  {localPdfPreview?.name || previewSavedAttachment?.name}
                </span>
                <button
                  type="button"
                  className="sd-pdf-preview-overlay__close"
                  onClick={() => {
                    closeLocalPdfPreview();
                    setPreviewSavedAttachment(null);
                  }}
                  title="Close preview"
                  aria-label="Close preview"
                >
                  ✕
                </button>
              </div>
              {localPdfPreview ? (
                <embed
                  title={localPdfPreview.name}
                  type="application/pdf"
                  src={`${localPdfPreview.url}#toolbar=1`}
                  className="sd-pdf-preview-overlay__frame"
                />
              ) : previewSavedAttachment ? (
                <TicketAttachmentPdfPreview
                  attachment={previewSavedAttachment}
                  filePath={String(
                    previewSavedAttachment.mediaUrl ??
                      collectAttachmentMediaPathsFromRecord(
                        previewSavedAttachment,
                        false
                      )[0] ??
                      previewSavedAttachment.url
                  )}
                  fallbackPaths={collectAttachmentMediaPathsFromRecord(
                    previewSavedAttachment,
                    false
                  )}
                  className="sd-pdf-preview-overlay__frame"
                />
              ) : null}
            </div>
          )}

          {/* Recording Active Overlay — live preview */}
          {recordingState === "recording" && (
            <div className="sd-recording-overlay" data-screenshot-skip="true">
              <div className="sd-recording-box">
                {/* Live screen preview */}
                <div className="sd-rec-preview-wrap">
                  <video
                    ref={videoPreviewRef}
                    autoPlay
                    muted
                    playsInline
                    className="sd-rec-preview"
                  />
                  {/* REC badge on top of video */}
                  <div className="sd-rec-badge">
                    <span className="sd-rec-dot"></span>
                    REC {formatRecordingTime(recordingSeconds)}
                  </div>
                </div>
                <div className="sd-rec-info">
                  <span>Screen is being recorded</span>
                </div>
                <button
                  className="sd-rec-stop-btn"
                  onClick={handleStopRecording}
                >
                  ■ Stop Recording
                </button>
              </div>
            </div>
          )}

          <div className="sd-panel-header">
            <div className="sd-panel-title">
              {!skipRequesterStep ? (
                <button
                  onClick={() => setStep("requester")}
                  className="sd-back-btn"
                  title="Back"
                >
                  <ArrowLeft size={15} />
                </button>
              ) : null}
              <span className="sd-dot"></span> {panelTitle}
            </div>
            <button
              onClick={closeWidget}
              className="sd-panel-close"
              aria-label="Close"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>

          {/* Step Progress */}
          {!skipRequesterStep ? (
            <div className="sd-step-bar">
              <div className="sd-step-item">
                <span className="sd-step-num done">✓</span>
                <span className="sd-step-label done">Your Info</span>
              </div>
              <div className="sd-step-divider done"></div>
              <div className="sd-step-item">
                <span className="sd-step-num active">2</span>
                <span className="sd-step-label active">Ticket Details</span>
              </div>
            </div>
          ) : null}

          <div className="sd-panel-body sd-panel-body--compact">
            {loadingTicket ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : (
              <>
            <h2 className="sd-panel-headline">{headline}</h2>
            <p className="sd-panel-sub">{panelSub}</p>

            {/* Manual Form Fields */}
            <div className="sd-manual-form">
              {isExternalUser ? (
                <div className="sd-field">
                  <label className="sd-field-label" htmlFor="sd-project">
                    Select Project <span className="sd-required">*</span>
                  </label>
                  <PtsListSelect
                    selectId="pts-project"
                    openSelectId={openPtsSelectId}
                    onOpenSelectIdChange={handleOpenSelectIdChange}
                    agentTheme={agentTheme}
                    value={ptsData.project}
                    disabled={ptsProjectsLoading}
                    hasError={!!ptsErrors.project}
                    placeholder={
                      ptsProjectsLoading
                        ? "Loading projects…"
                        : "-- Select Project --"
                    }
                    options={PTS_PROJECT_OPTIONS}
                    loading={ptsProjectsLoading}
                    searchable
                    searchPlaceholder="Search projects…"
                    maxVisible={8}
                    onChange={projectId => {
                      setPtsData(d => ({
                        ...d,
                        project: projectId,
                        task: "",
                      }));
                      setPtsErrors(er => ({ ...er, project: "" }));
                    }}
                  />
                  {ptsProjectsError && !ptsProjectsLoading ? (
                    <span className="sd-field-error">
                      Could not load projects. Please try again.
                    </span>
                  ) : null}
                  {ptsErrors.project ? (
                    <span className="sd-field-error">{ptsErrors.project}</span>
                  ) : null}
                </div>
              ) : null}

              {/* Subject */}
              <div className="sd-field">
                <label
                  className="sd-field-label sd-field-label--with-count"
                  htmlFor="sd-subject"
                >
                  <span>
                    Subject <span className="sd-required">*</span>
                  </span>
                  <span className="sd-char-count">
                    {ticketData.subject.length} / {SUBJECT_MAX_LENGTH}
                  </span>
                </label>
                <input
                  id="sd-subject"
                  type="text"
                  placeholder="Brief description of the issue..."
                  value={ticketData.subject}
                  maxLength={SUBJECT_MAX_LENGTH}
                  onChange={e => {
                    const subject = clampTicketSubject(e.target.value);
                    setTicketData(prev => ({
                      ...prev,
                      subject,
                    }));
                    if (ticketErrors.subject)
                      setTicketErrors(p => ({ ...p, subject: "" }));
                  }}
                  onPaste={e => handleSubjectInputPaste(e, SUBJECT_MAX_LENGTH)}
                  className={cn(
                    "sd-input",
                    ticketErrors.subject && "sd-input-error"
                  )}
                />
                {ticketErrors.subject && (
                  <span className="sd-field-error">{ticketErrors.subject}</span>
                )}
              </div>

              <div className="sd-field">
                <label className="sd-field-label" htmlFor="sd-type">
                  Ticket Type <span className="sd-required">*</span>
                </label>
                <PtsListSelect
                  selectId="ticket-type"
                  openSelectId={openPtsSelectId}
                  onOpenSelectIdChange={handleOpenSelectIdChange}
                  agentTheme={agentTheme}
                  value={ticketData.type}
                  hasError={!!ticketErrors.type}
                  placeholder="Select ticket type"
                  options={TICKET_TYPE_CREATE_OPTIONS}
                  searchable
                  searchPlaceholder="Search ticket type…"
                  maxVisible={5}
                  onChange={type => {
                    setTicketData(prev => ({ ...prev, type }));
                    if (ticketErrors.type)
                      setTicketErrors(p => ({ ...p, type: "" }));
                  }}
                />
                {ticketErrors.type ? (
                  <span className="sd-field-error">{ticketErrors.type}</span>
                ) : null}
              </div>

              {/* Category & Priority Row */}
              <div className="sd-row">
                <div className="sd-field">
                  <label className="sd-field-label" htmlFor="sd-category">
                    Category <span className="sd-required">*</span>
                  </label>
                  <PtsListSelect
                    selectId="ticket-category"
                    openSelectId={openPtsSelectId}
                    onOpenSelectIdChange={handleOpenSelectIdChange}
                    agentTheme={agentTheme}
                    value={ticketData.category}
                    hasError={!!ticketErrors.category}
                    placeholder="Select category"
                    options={CATEGORIES}
                    maxVisible={11}
                    onChange={category => {
                      setTicketData(prev => ({ ...prev, category }));
                      if (ticketErrors.category)
                        setTicketErrors(p => ({ ...p, category: "" }));
                    }}
                  />
                  {ticketErrors.category && (
                    <span className="sd-field-error">
                      {ticketErrors.category}
                    </span>
                  )}
                </div>

                <div className="sd-field">
                  <label className="sd-field-label" htmlFor="sd-priority">
                    Priority <span className="sd-required">*</span>
                  </label>
                  <PtsListSelect
                    selectId="ticket-priority"
                    openSelectId={openPtsSelectId}
                    onOpenSelectIdChange={handleOpenSelectIdChange}
                    agentTheme={agentTheme}
                    value={ticketData.priority}
                    disabled={prioritiesLoading}
                    hasError={!!ticketErrors.priority}
                    placeholder={
                      prioritiesLoading ? "Loading…" : "-- Select Priority --"
                    }
                    options={PRIORITY_OPTIONS}
                    loading={prioritiesLoading}
                    maxVisible={6}
                    emptyMessage="No priorities available"
                    onChange={priority => {
                      setTicketData(prev => ({ ...prev, priority }));
                      if (ticketErrors.priority)
                        setTicketErrors(p => ({ ...p, priority: "" }));
                    }}
                  />
                  {ticketErrors.priority && (
                    <span className="sd-field-error">
                      {ticketErrors.priority}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="sd-field sd-field--description">
                <label
                  className="sd-field-label sd-field-label--with-count"
                  htmlFor="sd-description"
                >
                  <span>
                    Description <span className="sd-required">*</span>
                  </span>
                  <span className="sd-char-count">
                    {richTextPlainTextLength(ticketData.description)} /{" "}
                    {DESCRIPTION_MAX_LENGTH}
                  </span>
                </label>
                <TicketDescriptionRichTextEditor
                  id="sd-description"
                  value={ticketData.description}
                  placeholder="Write something…"
                  maxPlainLength={DESCRIPTION_MAX_LENGTH}
                  error={Boolean(ticketErrors.description)}
                  disabled={submitting}
                  onChange={html => {
                    setTicketData(prev => ({
                      ...prev,
                      description: html,
                    }));
                    if (ticketErrors.description)
                      setTicketErrors(p => ({ ...p, description: "" }));
                  }}
                />
                {ticketErrors.description && (
                  <span className="sd-field-error">
                    {ticketErrors.description}
                  </span>
                )}
              </div>
            </div>

            {/* Saved attachments (edit) */}
            {isEditMode &&
            (existingAttachmentIds.length > 0 || editAttachments.length > 0) ? (
              <div className="sd-edit-attachments">
                <div className="sd-edit-attachments__header">
                  <div className="sd-edit-attachments__title">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    <span>Saved attachments</span>
                    {editAttachments.length > 0 ? (
                      <span className="sd-edit-attachments__count">
                        {editAttachments.length}
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="sd-edit-attachments__hint">
                  Files already on this ticket. Hover a card and use ✕ to remove
                  before saving.
                </p>
                {editAttachments.length === 0 ? (
                  <p className="sd-edit-attachments__loading">
                    Loading attachments…
                  </p>
                ) : (
                  <div className="sd-edit-attachments__grid">
                    {editAttachments.map(att => {
                      const mediaPaths = collectAttachmentMediaPathsFromRecord(
                        att,
                        false
                      );
                      const filePath =
                        att.mediaUrl ?? mediaPaths[0] ?? att.url;
                      const fallbackPaths = [
                        ...mediaPaths.filter(p => p !== filePath),
                        ...(att.mediaUrl && !mediaPaths.includes(att.mediaUrl)
                          ? [att.mediaUrl]
                          : []),
                      ];
                      const kind = attachmentKindLabel(att.name, att.type);
                      const sizeLabel = formatAttachmentSize(att.size);

                      return (
                        <div
                          key={att.id}
                          className={
                            isExternalAgentRole
                              ? "sd-edit-attach-card sd-edit-attach-card--ext-agent"
                              : "sd-edit-attach-card"
                          }
                        >
                          <button
                            type="button"
                            className="sd-edit-attach-card__remove"
                            onClick={() =>
                              handleRemoveExistingAttachment(att.id)
                            }
                            disabled={submitting}
                            title="Remove attachment"
                            aria-label={`Remove ${att.name}`}
                          >
                            ✕
                          </button>
                          <div className="sd-edit-attach-card__media">
                            {kind === "Image" ? (
                              <AuthenticatedMediaImage
                                filePath={String(filePath)}
                                fallbackPaths={fallbackPaths}
                                alt={att.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            ) : kind === "Video" ? (
                              <div className="sd-edit-attach-card__video-wrap">
                                <AuthenticatedMediaVideo
                                  fileId={att.id}
                                  filePath={String(filePath)}
                                  fallbackPaths={fallbackPaths}
                                  filenameHint={att.name}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    minHeight: 120,
                                    objectFit: "cover",
                                    display: "block",
                                    background: "#000",
                                  }}
                                />
                              </div>
                            ) : kind === "Pdf" ? (
                              <button
                                type="button"
                                className="sd-edit-attach-card__file-icon sd-edit-attach-card__file-icon--pdf"
                                onClick={() => {
                                  closeLocalPdfPreview();
                                  setPreviewSavedAttachment(att);
                                }}
                                title="Preview PDF"
                                style={{
                                  background: "none",
                                  border: "none",
                                  width: "100%",
                                  height: "100%",
                                  cursor: "pointer",
                                  padding: 0,
                                }}
                              >
                                <TicketAttachmentPdfPreview
                                  attachment={att}
                                  filePath={String(filePath)}
                                  fallbackPaths={fallbackPaths}
                                  className="sd-edit-attach-card__pdf-frame"
                                />
                              </button>
                            ) : (
                              <div className="sd-edit-attach-card__file-icon">
                                <svg
                                  viewBox="0 0 24 24"
                                  width="28"
                                  height="28"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                >
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <span>{att.name}</span>
                              </div>
                            )}
                          </div>
                          <div className="sd-edit-attach-card__footer">
                            <div
                              className="sd-edit-attach-card__name"
                              title={att.name}
                            >
                              {att.name}
                            </div>
                            <div className="sd-edit-attach-card__meta">
                              <span
                                className={`sd-edit-attach-card__badge sd-edit-attach-card__badge--${kind.toLowerCase()}`}
                              >
                                {kind}
                              </span>
                              {sizeLabel ? (
                                <span className="sd-edit-attach-card__size">
                                  {sizeLabel}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}

            <div
              className={`sd-attach-section${isEditMode ? " sd-attach-section--add-more" : ""}`}
            >
              <div className="sd-attach-header">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                <span>{isEditMode ? "Add more files" : "Attach Images, PDFs & Videos"}</span>
                {capturedScreenshots.length + recordedVideos.length + attachedDocuments.length > 0 && (
                  <span
                    style={{
                      marginLeft: "6px",
                      fontSize: "11px",
                      color: accentColor,
                      fontWeight: 600,
                    }}
                  >
                    {capturedScreenshots.length + recordedVideos.length + attachedDocuments.length} attached
                  </span>
                )}
              </div>
              <p className="sd-attach-hint">{TICKET_ATTACHMENT_UPLOAD_HINT}</p>

              {/* Image grid — thumbnails + plus tile + empty drop zone */}
              <div
                role="button"
                tabIndex={submitting || isCapturing ? -1 : 0}
                aria-label="Add JPG, PNG images, PDFs, or videos from your computer"
                onClick={handleAttachAreaClick}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    void openAttachmentPicker();
                  }
                }}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  padding: "10px",
                  border: "1.5px dashed #d1d5db",
                  borderRadius: "10px",
                  background: "#fafafa",
                  cursor: submitting || isCapturing ? "not-allowed" : "pointer",
                  minHeight: "76px",
                  alignItems: "center",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={e => {
                  if (!submitting && !isCapturing)
                    e.currentTarget.style.borderColor = accentColor;
                }}
                onMouseLeave={e =>
                  (e.currentTarget.style.borderColor = "#d1d5db")
                }
              >
                {/* Existing image thumbnails */}
                {capturedScreenshots.map((ss, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: "relative",
                      width: "76px",
                      height: "60px",
                      borderRadius: "7px",
                      overflow: "hidden",
                      border: "1.5px solid #e5e7eb",
                      flexShrink: 0,
                    }}
                    onClick={e => e.preventDefault()}
                  >
                    <img
                      src={ss.dataUrl}
                      alt={ss.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveScreenshot(idx);
                      }}
                      disabled={submitting}
                      style={{
                        position: "absolute",
                        top: "3px",
                        right: "3px",
                        background: "rgba(0,0,0,0.65)",
                        border: "none",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "9px",
                        lineHeight: 1,
                      }}
                      title="Remove"
                    >
                      ✕
                    </button>
                    {/* Filename tooltip */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        fontSize: "9px",
                        padding: "2px 4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {ss.name.length > 12
                        ? ss.name.slice(0, 10) + "…"
                        : ss.name}
                    </div>
                  </div>
                ))}

                {recordedVideos.map((v, idx) => (
                  <div
                    key={`new-video-${idx}`}
                    style={{
                      position: "relative",
                      width: "76px",
                      height: "60px",
                      borderRadius: "7px",
                      overflow: "hidden",
                      border: "1.5px solid #e5e7eb",
                      flexShrink: 0,
                      background: "#000",
                    }}
                    onClick={e => e.preventDefault()}
                  >
                    <video
                      src={recordedVideoPreviewUrls[idx]}
                      muted
                      playsInline
                      preload="metadata"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <button
                      type="button"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveVideo(idx);
                      }}
                      disabled={submitting}
                      style={{
                        position: "absolute",
                        top: "3px",
                        right: "3px",
                        background: "rgba(0,0,0,0.65)",
                        border: "none",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "9px",
                        lineHeight: 1,
                      }}
                      title="Remove"
                    >
                      ✕
                    </button>
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        fontSize: "9px",
                        padding: "2px 4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {v.name.length > 12 ? `${v.name.slice(0, 10)}…` : v.name}
                    </div>
                  </div>
                ))}

                {/* Plus tile */}
                <div
                  style={{
                    width: "76px",
                    height: "60px",
                    borderRadius: "7px",
                    border: `1.5px dashed ${accentColor}`,
                    background: accentLightBg,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: accentColor,
                    fontSize: "22px",
                    fontWeight: 300,
                    flexShrink: 0,
                    userSelect: "none",
                  }}
                >
                  <span style={{ lineHeight: 1 }}>+</span>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 600,
                      marginTop: "3px",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {capturedScreenshots.length + recordedVideos.length + attachedDocuments.length === 0
                      ? "Add File"
                      : "Add More"}
                  </span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={TICKET_ATTACHMENT_UPLOAD_ACCEPT}
                  multiple
                  tabIndex={-1}
                  aria-hidden
                  disabled={submitting || isCapturing}
                  style={{ display: "none" }}
                />
              </div>

              {attachedDocuments.some((d, idx) =>
                Boolean(attachedPdfPreviewUrls[idx])
              ) ? (
                <div className="sd-pdf-inline-previews">
                  {attachedDocuments.map((d, idx) => {
                    const url = attachedPdfPreviewUrls[idx];
                    if (!url) return null;
                    return (
                      <div key={`pdf-preview-${d.name}-${idx}`} className="sd-pdf-inline-preview">
                        <div className="sd-pdf-inline-preview__bar">
                          <span>{d.name}</span>
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              handlePreviewDocument(d);
                            }}
                          >
                            Open larger
                          </button>
                        </div>
                        <embed
                          title={d.name}
                          type="application/pdf"
                          src={`${url}#toolbar=1`}
                          className="sd-pdf-inline-preview__frame"
                        />
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {/* Capture buttons row */}
              <div className="sd-attach-btns">
                <button
                  type="button"
                  className="sd-attach-btn"
                  onClick={handleCaptureCurrentPage}
                  disabled={
                    submitting || isCapturing || recordingState === "recording"
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  This Page
                </button>
                <button
                  type="button"
                  className="sd-attach-btn"
                  onClick={handleCaptureSystemScreen}
                  disabled={
                    submitting || isCapturing || recordingState === "recording"
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                  {isCapturing ? "Waiting..." : "Any Window"}
                </button>
                <button
                  type="button"
                  className={`sd-attach-btn${recordingState === "recording" ? "sd-attach-btn-rec" : ""}`}
                  onClick={
                    recordingState === "idle"
                      ? handleStartRecording
                      : handleStopRecording
                  }
                  disabled={submitting || isCapturing}
                >
                  {recordingState === "recording" ? (
                    <>
                      <span className="sd-rec-dot-sm"></span>■{" "}
                      {formatRecordingTime(recordingSeconds)}
                    </>
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <circle
                          cx="12"
                          cy="12"
                          r="4"
                          fill="#dc2626"
                          stroke="#dc2626"
                        />
                      </svg>
                      Record
                    </>
                  )}
                </button>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#9ca3af",
                    alignSelf: "center",
                  }}
                >
                  or Ctrl+V paste
                </span>
              </div>

              {/* Recorded videos list */}
              {recordedVideos.length > 0 && (
                <div className="sd-video-list">
                  {recordedVideos.map((v, idx) => (
                    <div key={idx} className="sd-video-item">
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="#dc2626"
                        strokeWidth="2"
                        style={{ flexShrink: 0 }}
                      >
                        <rect x="2" y="4" width="20" height="16" rx="3" />
                        <polygon
                          points="10,9 16,12 10,15"
                          fill="#dc2626"
                          stroke="none"
                        />
                      </svg>
                      <span className="sd-video-name">
                        {v.name.length > 24
                          ? v.name.slice(0, 22) + "…"
                          : v.name}
                      </span>
                      <span className="sd-video-size">
                        {(v.blob.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      <button
                        type="button"
                        className="sd-video-remove"
                        onClick={() => handleRemoveVideo(idx)}
                        disabled={submitting}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Attached PDF / DOC / ZIP documents list */}
              {attachedDocuments.length > 0 && (
                <div className="sd-video-list">
                  {attachedDocuments.map((d, idx) => {
                    const kind = attachedDocumentKind(d.name);
                    const canPreviewPdf = looksLikeViewPdf(d.name, d.blob.type);
                    return (
                    <div key={idx} className="sd-video-item">
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke={kind.color}
                        strokeWidth="2"
                        style={{ flexShrink: 0 }}
                      >
                        <path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                        <path d="M14 2v5h5" />
                        <text
                          x="12"
                          y="17"
                          textAnchor="middle"
                          fontSize="7"
                          fill={kind.color}
                          stroke="none"
                          fontWeight="bold"
                        >
                          {kind.label}
                        </text>
                      </svg>
                      {canPreviewPdf ? (
                        <button
                          type="button"
                          className="sd-video-name"
                          onClick={() => handlePreviewDocument(d)}
                          title="Preview PDF"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            textAlign: "left",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {d.name.length > 24
                            ? d.name.slice(0, 22) + "…"
                            : d.name}
                        </button>
                      ) : (
                        <span className="sd-video-name">
                          {d.name.length > 24
                            ? d.name.slice(0, 22) + "…"
                            : d.name}
                        </span>
                      )}
                      <span className="sd-video-size">
                        {(d.blob.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      {canPreviewPdf ? (
                        <button
                          type="button"
                          className="sd-video-remove"
                          onClick={() => handlePreviewDocument(d)}
                          disabled={submitting}
                          title="Preview"
                          style={{ color: "inherit" }}
                        >
                          <Eye size={13} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="sd-video-remove"
                        onClick={() => handleRemoveDocument(idx)}
                        disabled={submitting}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!isExternalUser ? (
            <div className="sd-form-bottom">
              <div className="sd-pts-checkbox-stack">
                <label className="sd-pts-checkbox-row">
                  <input
                    type="checkbox"
                    className="sd-pts-checkbox"
                    checked={includePtsFields}
                    onChange={e => {
                      const checked = e.target.checked;
                      setIncludePtsFields(checked);
                      if (checked) {
                        setShouldLoadPriorities(true);
                      } else {
                        setOpenPtsSelectId(null);
                        setPtsData({
                          project: "",
                          task: "",
                          estimationHours: "",
                          workType: "",
                        });
                        setPtsErrors({});
                        resetChangeRequirementFields();
                      }
                    }}
                  />
                  <span className="sd-pts-checkbox-label">
                    Include PTS task details
                  </span>
                </label>
                {canIncludeChangeRequirement ? (
                  <label
                    className={cn(
                      "sd-pts-checkbox-row",
                      !includePtsFields && "sd-pts-checkbox-row--disabled"
                    )}
                    title={
                      includePtsFields
                        ? undefined
                        : "Tick Include PTS task details first"
                    }
                  >
                    <input
                      type="checkbox"
                      className="sd-pts-checkbox"
                      checked={includeChangeRequirement}
                      disabled={!includePtsFields}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (!includePtsFields) return;
                        setIncludeChangeRequirement(checked);
                        if (!checked) {
                          setChangeReqData({ ...EMPTY_CHANGE_REQ_DATA });
                          setChangeReqErrors({});
                        }
                      }}
                    />
                    <span className="sd-pts-checkbox-label">
                      Include change requirement
                    </span>
                  </label>
                ) : null}
              </div>

              {includePtsFields && (
                <div className="sd-pts-form sd-pts-form--inline">
                  <p className="sd-pts-inline-heading">PTS Task Details</p>
                  <div className="sd-pts-field">
                    <label className="sd-pts-label">
                      Select Project <span className="sd-pts-required">*</span>
                    </label>
                    <PtsListSelect
                      selectId="pts-project"
                      openSelectId={openPtsSelectId}
                      onOpenSelectIdChange={handleOpenSelectIdChange}
                    agentTheme={agentTheme}
                      value={ptsData.project}
                      disabled={ptsProjectsLoading}
                      hasError={!!ptsErrors.project}
                      placeholder={
                        ptsProjectsLoading
                          ? "Loading projects…"
                          : "-- Select Project --"
                      }
                      options={PTS_PROJECT_OPTIONS}
                      loading={ptsProjectsLoading}
                      searchable
                      searchPlaceholder="Search projects…"
                      maxVisible={8}
                      onChange={projectId => {
                        setPtsData(d => ({
                          ...d,
                          project: projectId,
                          task: "",
                        }));
                        setPtsErrors(er => ({ ...er, project: "" }));
                      }}
                    />
                    {ptsProjectsError && !ptsProjectsLoading && (
                      <span className="sd-pts-error">
                        Could not load projects. Please try again.
                      </span>
                    )}
                    {ptsErrors.project && (
                      <span className="sd-pts-error">{ptsErrors.project}</span>
                    )}
                  </div>
                  <div className="sd-pts-field">
                    <label className="sd-pts-label">
                      Select Task <span className="sd-pts-required">*</span>
                    </label>
                    <PtsListSelect
                      selectId="pts-task"
                      openSelectId={openPtsSelectId}
                      onOpenSelectIdChange={handleOpenSelectIdChange}
                    agentTheme={agentTheme}
                      value={ptsData.task}
                      disabled={ptsTasksLoading && !!ptsData.project}
                      hasError={!!ptsErrors.task}
                      placeholder={
                        !ptsData.project
                          ? "-- Select Task --"
                          : ptsTasksLoading
                            ? "Loading tasks…"
                            : "-- Select Task --"
                      }
                      options={PTS_TASK_OPTIONS}
                      loading={ptsTasksLoading && !!ptsData.project}
                      searchable
                      searchPlaceholder="Search tasks…"
                      maxVisible={8}
                      emptyMessage={
                        !ptsData.project
                          ? "Please select a project first"
                          : "No tasks found for this project"
                      }
                      onChange={taskId => {
                        setPtsData(d => ({ ...d, task: taskId }));
                        setPtsErrors(er => ({ ...er, task: "" }));
                      }}
                    />
                    {ptsTasksError && !ptsTasksLoading && ptsData.project && (
                      <span className="sd-pts-error">
                        Could not load tasks. Please try again.
                      </span>
                    )}
                    {ptsErrors.task && (
                      <span className="sd-pts-error">{ptsErrors.task}</span>
                    )}
                  </div>
                  <div className="sd-pts-grid-2">
                    <div className="sd-pts-field">
                      <label className="sd-pts-label">
                        Est. Hours <span className="sd-pts-required">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="999"
                        step="1"
                        className={`sd-pts-input${ptsErrors.estimationHours ? "sd-pts-input--error" : ""}`}
                        placeholder="e.g. 4"
                        value={ptsData.estimationHours}
                        onKeyDown={e => {
                          if ([".", ",", "e", "E", "+", "-"].includes(e.key))
                            e.preventDefault();
                        }}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, "");
                          setPtsData(d => ({ ...d, estimationHours: val }));
                          const hrs = parseInt(val, 10);
                          if (!val) {
                            setPtsErrors(er => ({
                              ...er,
                              estimationHours: "Estimation Hours is required.",
                            }));
                          } else if (isNaN(hrs) || hrs < 1) {
                            setPtsErrors(er => ({
                              ...er,
                              estimationHours: "Minimum 1 hour required.",
                            }));
                          } else if (hrs > 999) {
                            setPtsErrors(er => ({
                              ...er,
                              estimationHours: "Cannot exceed 999 hours.",
                            }));
                          } else {
                            setPtsErrors(er => ({
                              ...er,
                              estimationHours: "",
                            }));
                          }
                        }}
                      />
                      {ptsErrors.estimationHours ? (
                        <span className="sd-pts-error">
                          {ptsErrors.estimationHours}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "10.5px",
                            color: "#9ca3af",
                            fontWeight: 500,
                            marginTop: "3px",
                          }}
                        >
                          Whole numbers only (e.g. 1, 2, 3)
                        </span>
                      )}
                    </div>
                    <div className="sd-pts-field">
                      <label className="sd-pts-label">
                        Work Type <span className="sd-pts-required">*</span>
                      </label>
                      <PtsListSelect
                        selectId="pts-work-type"
                        openSelectId={openPtsSelectId}
                        onOpenSelectIdChange={handleOpenSelectIdChange}
                    agentTheme={agentTheme}
                        value={ptsData.workType}
                        disabled={ptsWorkTypeLoading}
                        hasError={!!ptsErrors.workType}
                        placeholder={
                          ptsWorkTypeLoading
                            ? "Loading…"
                            : "-- Select Work Type --"
                        }
                        options={PTS_WORK_TYPE_OPTIONS}
                        maxVisible={8}
                        loading={ptsWorkTypeLoading}
                        searchable
                        searchPlaceholder="Search work types…"
                        onChange={workTypeId => {
                          setPtsData(d => ({ ...d, workType: workTypeId }));
                          setPtsErrors(er => ({ ...er, workType: "" }));
                        }}
                      />
                      {ptsErrors.workType && (
                        <span className="sd-pts-error">
                          {ptsErrors.workType}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {includePtsFields && includeChangeRequirement ? (
                <div className="sd-pts-form sd-pts-form--inline">
                  <p className="sd-pts-inline-heading">Change requirement</p>
                  <div className="sd-pts-grid-3">
                    <div className="sd-pts-field">
                      <label className="sd-pts-label">
                        Requested date <span className="sd-pts-required">*</span>
                      </label>
                      <input
                        type="date"
                        className={`sd-pts-input${changeReqErrors.requestedDate ? " sd-pts-input--error" : ""}`}
                        value={changeReqData.requestedDate}
                        onChange={e => {
                          const requestedDate = e.target.value;
                          setChangeReqData(d => ({ ...d, requestedDate }));
                          setChangeReqErrors(er => ({
                            ...er,
                            requestedDate: "",
                          }));
                        }}
                      />
                      {changeReqErrors.requestedDate ? (
                        <span className="sd-pts-error">
                          {changeReqErrors.requestedDate}
                        </span>
                      ) : null}
                    </div>
                    <div className="sd-pts-field">
                      <label className="sd-pts-label">
                        Expected completion{" "}
                        <span className="sd-pts-required">*</span>
                      </label>
                      <input
                        type="date"
                        className={`sd-pts-input${changeReqErrors.expectedCompletion ? " sd-pts-input--error" : ""}`}
                        value={changeReqData.expectedCompletion}
                        min={changeReqData.requestedDate || undefined}
                        onChange={e => {
                          const expectedCompletion = e.target.value;
                          setChangeReqData(d => ({
                            ...d,
                            expectedCompletion,
                          }));
                          setChangeReqErrors(er => ({
                            ...er,
                            expectedCompletion: "",
                          }));
                        }}
                      />
                      {changeReqErrors.expectedCompletion ? (
                        <span className="sd-pts-error">
                          {changeReqErrors.expectedCompletion}
                        </span>
                      ) : null}
                    </div>
                    <div className="sd-pts-field">
                      <label className="sd-pts-label">
                        Assign employees{" "}
                        <span className="sd-pts-required">*</span>
                      </label>
                      <PtsListSelect
                        selectId="change-req-employee"
                        openSelectId={openPtsSelectId}
                        onOpenSelectIdChange={handleOpenSelectIdChange}
                        agentTheme={agentTheme}
                        value={changeReqData.assignedEmployee}
                        disabled={changeReqEmployeesLoading}
                        hasError={!!changeReqErrors.assignedEmployee}
                        placeholder={
                          changeReqEmployeesLoading
                            ? "Loading employees…"
                            : "Select employees"
                        }
                        options={CHANGE_REQ_EMPLOYEE_OPTIONS}
                        loading={changeReqEmployeesLoading}
                        searchable
                        multiple
                        searchPlaceholder="Search employees…"
                        maxVisible={8}
                        emptyMessage="No employees available"
                        onChange={employeeId => {
                          setChangeReqData(d => ({
                            ...d,
                            assignedEmployee: employeeId,
                          }));
                          setChangeReqErrors(er => ({
                            ...er,
                            assignedEmployee: "",
                          }));
                        }}
                      />
                      {changeReqErrors.assignedEmployee ? (
                        <span className="sd-pts-error">
                          {changeReqErrors.assignedEmployee}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            ) : null}
              </>
            )}
          </div>

          <div className="sd-panel-footer">
            <div className="sd-actions">
              {!skipRequesterStep ? (
                <button
                  className="sd-btn"
                  onClick={() => setStep("requester")}
                  disabled={submitting}
                >
                  Back
                </button>
              ) : null}
              <button
                className="sd-btn sd-btn-primary"
                onClick={() => void handleSubmit()}
                disabled={submitting || loadingTicket}
              >
                {submitting ? (
                  <>
                    <span className="sd-spinner"></span>{" "}
                    {isEditMode ? "Saving…" : "Sending…"}
                  </>
                ) : (
                  submitLabel
                )}
              </button>
              {showCopyTicketButton ? (
                <button
                  type="button"
                  className="sd-btn"
                  title="Create this ticket and keep the form filled to create another copy"
                  onClick={() => void handleSubmit({ keepFormOpen: true })}
                  disabled={submitting || loadingTicket}
                >
                  {submitting ? (
                    <>
                      <span className="sd-spinner"></span> Creating…
                    </>
                  ) : (
                    "Copy Ticket"
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </ConfigProvider>
  );
}
